/**
 * WHAT THE ENTITY CHAT SAYS — one place for every reply line, so the card and the transcript agree.
 * Q10: the rest applies and every refusal and gap is named; Q19: every name picked is in the change lines;
 * 3a final review I7: parts of the request that couldn't be read are counted, never silent.
 */
import type { EntityStep } from '../route/entity-route';
import type { EntityEdit } from '../zs-entity.types';
import type { Entity } from '../../../../model/entity.model';
import type { FixReport } from '../../sync-entities.types';
import type { HeldAction } from '../route/action-route';
import { isFinalized, sameEntityName } from '../rules/entity-form-rules';

export type ApplyStep = Extract<EntityStep, { kind: 'apply' }>;

const andList = (xs: string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);
const gapLine = (gaps: readonly string[]): string => `Not possible on the entity page: ${gaps.join('; ')}.`;
/** Case-insensitive, null-safe name compare (fix round 1, FIX 2). */
// Unused since plan 4a final review (K1) — sameEntityName replaces it.
const same = (a: string | null, b: string): boolean => !!a && a.toLowerCase() === b.toLowerCase();

/** The entities a request's waiting edits are for, in order, once each. */
export function waitingNames(edits: readonly { entity: string | null }[]): string[] {
  return [...new Set(edits.map((e) => e.entity).filter((n): n is string => !!n))];
}

/**
 * Plan 4a final review (I2) — split waiting edits into those for entities that LINK to `first` (any of that
 * entity's edits references it) and the rest. An entity's edits always travel together.
 */
export function splitLinked(waiting: readonly EntityEdit[], first: string): { linked: EntityEdit[]; rest: EntityEdit[] } {
  const linkers = waitingNames(waiting.filter((e) => sameEntityName(e.reference, first)));
  const isLinker = (e: EntityEdit): boolean => linkers.some((n) => sameEntityName(e.entity, n));
  return { linked: waiting.filter(isLinker), rest: waiting.filter((e) => !isLinker(e)) };
}

export const resumeText = (name: string): string => `${name} is finalized — continuing.`;

// Was (2026-10-03, plan 4a final review — "is done" was also shown when the first entity was closed
// WITHOUT saving, which is wrong there; renamed to "is closed", true in both cases):
// /** Fix round 1 (2026-10-03, FIX 2) — the resume card prefix for edits that don't link to the first entity:
//  * they continue once its editor is closed, saved or not, never needing Finalize. */
// export const continueText = (name: string): string => `${name} is done — continuing.`;
/** The resume card prefix for edits that don't link to the first entity: they continue once its editor is
 * closed, saved or not, never needing Finalize — and also when it was closed without saving. */
export const continueText = (name: string): string => `${name} is closed — continuing.`;

export const localDropText = (name: string, names: string[]): string =>
  `${name} is local, so nothing can link to it — I've dropped ${andList(names)}.`;

/** Plan 4a final review (T3 deferred) — a linked first entity closed without saving: nothing can land on it. */
export const closedDropText = (name: string, names: string[]): string =>
  `${name} was closed without saving, so nothing can link to it — I've dropped ${andList(names)}.`;
/** Plan 4a final review (I3) — said on the next card when a new request drops the last one's waiting edits. */
export const droppedText = (names: string[]): string => `I've dropped ${andList(names)} from your last request.`;

export function applyReplyText(step: ApplyStep, dropped: number): string {
  const lines = [...step.changes];
  for (const d of step.declined) lines.push(`Not done: ${d}`);
  if (step.gaps.length) lines.push(gapLine(step.gaps));
  if (dropped > 0) lines.push(`${dropped} ${dropped === 1 ? 'part' : 'parts'} of that couldn't be read.`);
  // Was (2026-10-03, plan 4a final review — I2, a local first entity never finalizes, so what links to it
  // must be dropped at once, not told to wait for a Finalize the page doesn't offer; Q16 amended):
  // if (step.waiting.length) {
  //   // Was (2026-10-03, plan 4a — resuming these automatically when the entity is finalized is now built
  //   // (Q5, checkWaiting), so the reply no longer tells the builder to ask again):
  //   // const names = [...new Set(step.waiting.map((e) => e.entity).filter((n): n is string => !!n))];
  //   // const who = step.entity.name || 'this entity';
  //   // lines.push(`Next: ${andList(names)} — finalize ${who}, then ask me for ${andList(names)}.`);
  //   // Was (2026-10-03, fix round 1 — FIX 2, every waiting request said "finalize", even when none of its
  //   // edits actually link to the entity it is waiting on — those only need its editor closed):
  //   // const names = waitingNames(step.waiting);
  //   // const who = step.entity.name || 'this entity';
  //   // lines.push(`Next: ${andList(names)} — finalize ${who} and I'll continue.`);
  //   const names = waitingNames(step.waiting);
  //   const who = step.entity.name || 'this entity';
  //   const linked = step.waiting.some((e) => same(e.reference, step.entity.name));
  //   lines.push(linked
  //     ? `Next: ${andList(names)} — finalize ${who} and I'll continue.`
  //     : `Next: ${andList(names)} — save or close ${who} and I'll continue.`);
  // }
  if (step.waiting.length) {
    const who = step.entity.name || 'this entity';
    const { linked, rest } = splitLinked(step.waiting, step.entity.name);
    if (step.entity.status === 'LOCAL' && linked.length) {
      // A local first entity: what links to it can never land — dropped now, not after a Finalize the page
      // doesn't offer (Q16 amended).
      lines.push(localDropText(who, waitingNames(linked)));
      if (rest.length) lines.push(`Next: ${andList(waitingNames(rest))} — save or close ${who} and I'll continue.`);
    } else if (linked.length && !isFinalized(step.entity.status)) {
      lines.push(`Next: ${andList(waitingNames(step.waiting))} — finalize ${who} and I'll continue.`);
    } else {
      // Nothing links to it, or it is already finalized: closing its editor is enough.
      const action = isFinalized(step.entity.status) ? 'update' : 'save';
      lines.push(`Next: ${andList(waitingNames(step.waiting))} — ${action} or close ${who} and I'll continue.`);
    }
  }
  return lines.join('\n');
}

export function declineReplyText(text: string, gaps: readonly string[]): string {
  return gaps.length ? `${text}\n${gapLine(gaps)}` : text;
}

export function reminderText(count: number, action: 'Save' | 'Update'): string {
  return `${count} unsaved changes — ${action} to keep them.`;
}

export function switchQuestion(from: string, to: string, unsaved: number, action: 'Save' | 'Update'): string {
  return `${from} has ${unsaved} unsaved ${unsaved === 1 ? 'change' : 'changes'}. ${action} it before I open ${to}?`;
}

// ── Held actions (plan 4b) ───────────────────────────────────────────────────────────────────────────

export const heldText = (label: string): string => `${label} — approve it in the bar below, or cancel.`;
export const notDoneText = (label: string): string => `Not done: ${label}.`;
/** Plan 4b final review (M4) — approving after the entity was closed says so, instead of claiming a different
 * entity is open. */
export const notOpenText = (name: string): string => `${name} isn't open any more, so nothing changed — ask again.`;
export const PERMISSIONS_FAILED_TEXT = 'The permissions didn\'t save — nothing changed.';
export const ACTION_FAILED_TEXT = 'That didn\'t finish — the page reported an error.';

export function importReplyText(res: { done: string[]; error: string | null }, names: string[]): string {
  // Plan 4b fix round 1 (F3) — every one was imported meanwhile: the runner skipped them all.
  if (!res.done.length && !res.error) return `Nothing imported — ${andList(names)} ${names.length === 1 ? 'is' : 'are'} already there.`;
  const lines: string[] = [];
  if (res.done.length) lines.push(`Imported ${andList(res.done)}.`);
  if (res.error) lines.push(`The import stopped before ${andList(names.slice(res.done.length))}: ${res.error}`);
  return lines.join('\n');
}

/** Q30 — the Sync report: entities checked, what was built or fixed, and the columns left behind. */
export function syncReplyText(res: { report: FixReport | null; error: string | null }, entities: readonly Entity[]): string {
  // Was (2026-10-03, plan 4b Task 5 fix round 1 — F6, an error already ending in a full stop got a second one):
  // if (res.error || !res.report) return `Sync didn't finish: ${res.error ?? 'no report came back'}.`;
  if (res.error || !res.report) return `Sync didn't finish: ${(res.error ?? 'no report came back').replace(/\.$/, '')}.`;
  const r = res.report;
  const nameOf = (id: string): string => entities.find((e) => e.id === id)?.name ?? id;
  const checked = r.created.length + r.updated.length + r.skipped.length + r.failed.length;
  const lines = [`Sync checked ${checked} ${checked === 1 ? 'entity' : 'entities'}: ${r.created.length} built, ${r.updated.length} updated.`];
  if (r.failed.length) lines.push(`Failed: ${r.failed.map((f) => `${nameOf(f.id)} (${f.error})`).join('; ')}.`);
  const other = r.fkFailed.length + r.hasuraFailed.length;
  if (other) lines.push(`${other} other ${other === 1 ? 'problem' : 'problems'} — see the Sync banner on the page.`);
  if (r.orphanedColumns.length) {
    lines.push(`Columns left behind by removed or renamed fields: ${r.orphanedColumns.map((o) => `${nameOf(o.table)}: ${o.columns.join(', ')}`).join('; ')}.`);
  }
  return lines.join('\n');
}

export function permissionsDoneText(a: Extract<HeldAction, { kind: 'permissions' }>): string {
  if (!a.next) return `Removed ${a.roleName} from ${a.entityName}.`;
  const next = a.next;
  const can = (['create', 'read', 'update', 'delete'] as const).filter((w) => next[w]);
  return `${a.roleName} on ${a.entityName} can now: ${can.length ? andList([...can]) : 'nothing yet'}.`;
}
